import { Height, Weight, WeightUnits } from "./Details";
import { Diet } from "./Goals";
import { MacroNutrientRatio } from "./MacroNutrient";

export class Util {
  static CM_CONST = 30.48;
  static LBS_CONST = 2.20462;

  static graphState = {
    labels: ["Protein", "Carbs", "Fats"],
    datasets: [
      {
        label: "Macro Nutrients",
        backgroundColor: ["#03bce4", "#f4a238", "#d3ad08"],
        hoverBackgroundColor: ["#03bce4", "#f4a238", "#d3ad08"],
        data: [30, 35, 35],
      },
    ],
  };

  static validateAge(age: number): boolean {
    if (age < 18 || age > 120) {
      return false;
    } else {
      return true;
    }
  }

  static validateWeight(weight: Weight) {
    if (typeof weight.weightKg !== "undefined") {
      if (weight.weightKg > 227 || weight.weightKg < 23) {
        return false;
      } else {
        return true;
      }
    } else if (typeof weight.weightLbs !== "undefined") {
      if (weight.weightLbs > 500 || weight.weightLbs < 50) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  static convertKgToLbs(kg: number): number {
    return Math.round(kg * this.LBS_CONST);
  }

  static convertLbsToKg(lbs: number): number {
    return Math.round((lbs / this.LBS_CONST) * 100) / 100;
  }

  static getWeight(weight: Weight): string {
    if (weight.selectedUnit === WeightUnits.kg) {
      return `${weight.weightKg}kg`;
    } else {
      return `${weight.weightLbs}lbs`;
    }
  }
  static validateHeight(height: Height) {
    if (
      typeof height.heightFt !== "undefined" &&
      typeof height.heightInch !== "undefined" &&
      !isNaN(height.heightFt) &&
      !isNaN(height.heightInch)
    ) {
      if (height.heightFt > 9 || height.heightFt < 2) {
        return false;
      } else if (height.heightInch > 11 || height.heightInch < 0) {
        return false;
      } else {
        return true;
      }
    } else if (
      typeof height.heightCm !== "undefined" &&
      !isNaN(height.heightCm)
    ) {
      if (height.heightCm > 300 || height.heightCm < 60) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  static convertFtToCm(feet: number, inches: number): number {
    let cmHeight = feet * Util.CM_CONST + inches * (Util.CM_CONST / 12);
    return Math.round(cmHeight);
  }

  static convertCmToFt(cm: number): { feet: number; inches: number } {
    const feet = Math.floor(cm / Util.CM_CONST);
    const inches = (cm % Util.CM_CONST) / (Util.CM_CONST / 12);
    return { feet: Math.round(feet), inches: Math.round(inches) };
  }

  static getMacroNutrientRatio(eatingStyle: Diet): MacroNutrientRatio {
    switch (eatingStyle) {
      case Diet.Anything:
      case Diet.Mediterranean:
      case Diet.Paleo:
      case Diet.Vegetarian:
        return { protein: 30, carbs: 35, fats: 35 };
      case Diet.Ketogenic:
        return { protein: 20, carbs: 10, fats: 70 };
      case Diet.PlantBased:
        return { protein: 30, carbs: 50, fats: 20 };
    }
  }

  static getMacroNutrientSuggestion(eatingStyle: Diet): MacroNutrientRatio[] {
    switch (eatingStyle) {
      case Diet.Anything:
      case Diet.Vegetarian:
        return [
          { protein: 30, carbs: 35, fats: 35, label: "Balanced" },
          { protein: 30, carbs: 50, fats: 20, label: "Low-Fat" },
          { protein: 30, carbs: 20, fats: 50, label: "Low-Carb" },
        ];
      case Diet.Mediterranean:
      case Diet.Paleo:
        return [
          { protein: 30, carbs: 35, fats: 35, label: "Balanced" },
          { protein: 30, carbs: 20, fats: 50, label: "Low-Carb" },
        ];

      case Diet.Ketogenic:
        return [{ protein: 20, carbs: 10, fats: 70, label: "Very-Low-Carb" }];
      case Diet.PlantBased:
        return [
          { protein: 30, carbs: 35, fats: 35, label: "Balanced" },
          { protein: 30, carbs: 50, fats: 20, label: "Low-Fat" },
          { protein: 30, carbs: 20, fats: 50, label: "Low-Carb" },
          { protein: 20, carbs: 70, fats: 10, label: "Very-Low-Fat" },
        ];
    }
  }

  static getProteinRange(eatingStyle: Diet): any {
    switch (eatingStyle) {
      case Diet.Anything:
      case Diet.Mediterranean:
      case Diet.Paleo:
      case Diet.Vegetarian:
        return { min: 20, max: 45 };
      case Diet.Ketogenic:
      case Diet.PlantBased:
        return { min: 20, max: 35 };
    }
  }

  static getCarbRange(eatingStyle: Diet): any {
    switch (eatingStyle) {
      case Diet.Anything:
      case Diet.Vegetarian:
        return { min: 20, max: 60 };
      case Diet.Mediterranean:
      case Diet.Paleo:
        return { min: 20, max: 50 };
      case Diet.Ketogenic:
        return { min: 5, max: 15 };
      case Diet.PlantBased:
        return { min: 20, max: 70 };
    }
  }

  static getFatRange(eatingStyle: Diet): any {
    switch (eatingStyle) {
      case Diet.Anything:
      case Diet.Vegetarian:
        return { min: 20, max: 50 };
      case Diet.Mediterranean:
        return { min: 30, max: 55 };
      case Diet.Paleo:
        return { min: 30, max: 60 };
      case Diet.Ketogenic:
        return { min: 50, max: 75 };
      case Diet.PlantBased:
        return { min: 10, max: 50 };
    }
  }
}
